const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const INFO_LINE_1 =
  'Your free search data is ready! This file includes basic people information. Need contact email addresses? Go back and click the "Download Data" button to purchase and download the complete Excel file with email contacts.';

const INFO_LINE_2 =
  'Want to reach these people instantly? Go back and click the "Send Emails" button to launch an automated email campaign. Your first 50 emails are absolutely free!';

const COLUMNS = [
  { key: "name", label: "Name", width: 28 },
  { key: "firstName", label: "First Name", width: 18 },
  { key: "lastName", label: "Last Name", width: 18 },
  { key: "jobTitle", label: "Job Title", width: 34 },
  { key: "company", label: "Company", width: 32 },
  { key: "companyWebsite", label: "Company Website", width: 28 },
  { key: "organisationName", label: "organisation.name", width: 32 },
  { key: "cityAvailable", label: "City Available", width: 18 },
  { key: "countryAvailable", label: "Country Available", width: 20 },
  { key: "stateAvailable", label: "State Available", width: 18 },
  { key: "title", label: "Title", width: 34 },
  { key: "status", label: "Status", width: 14 },
];

const textEncoder = new TextEncoder();

const escapeXml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const cleanValue = (value) => {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  return String(value).trim();
};

const toTitleCase = (value = "") =>
  cleanValue(value)
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const yesNo = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";

  const normalized = cleanValue(value).toLowerCase();
  if (!normalized) return "No";
  if (["yes", "true", "1", "available"].includes(normalized)) return "Yes";
  if (["no", "false", "0", "not available"].includes(normalized)) return "No";

  return cleanValue(value);
};

const getOrganisation = (person) => person.organization || person.organisation || {};

const buildPersonRow = (person) => {
  const organisation = getOrganisation(person);
  const firstName = person.first_name || person["First Name"] || "";
  const lastName = person?.last_name_obfuscated?.includes("*")
    ? ""
    : person.last_name || person["Last Name"] || "";
  const name =
    person.full_name ||
    person.name ||
    [firstName, lastName].filter(Boolean).join(" ") ||
    person["Full name"] ||
    "";
  const jobTitle = person.title || person.job_title || person["Job title"] || "";
  const company =
    person.company_name ||
    person["Company Name"] ||
    organisation.name ||
    person.organization_name ||
    "";
  const companyWebsite =
    organisation.website_url ||
    organisation.website ||
    organisation.primary_domain ||
    person.domain ||
    person.company_website ||
    person["Company Website"] ||
    "";

  return {
    name: toTitleCase(name),
    firstName: toTitleCase(firstName),
    lastName: toTitleCase(lastName),
    jobTitle: cleanValue(jobTitle),
    company: toTitleCase(company),
    companyWebsite: cleanValue(companyWebsite),
    organisationName: toTitleCase(organisation.name || person.organization_name || ""),
    cityAvailable: yesNo(person.has_city ?? organisation.has_city),
    countryAvailable: yesNo(person.has_country ?? organisation.has_country),
    stateAvailable: yesNo(person.has_state ?? organisation.has_state),
    title: cleanValue(person.title || ""),
    status: "Verified",
  };
};

const getColumnName = (index) => {
  let name = "";
  let value = index;

  while (value > 0) {
    const modulo = (value - 1) % 26;
    name = String.fromCharCode(65 + modulo) + name;
    value = Math.floor((value - modulo) / 26);
  }

  return name;
};

const stringCell = (cellRef, value, styleId = 0) =>
  `<c r="${cellRef}" t="inlineStr"${styleId ? ` s="${styleId}"` : ""}><is><t>${escapeXml(
    value,
  )}</t></is></c>`;

const buildWorksheetXml = (rows) => {
  const lastColumn = getColumnName(COLUMNS.length);
  const headerRowIndex = 4;
  const dataStartRowIndex = 5;
  const lastRow = Math.max(dataStartRowIndex + rows.length - 1, headerRowIndex);

  const columnWidths = COLUMNS.map(
    (column, index) =>
      `<col min="${index + 1}" max="${index + 1}" width="${column.width}" customWidth="1"/>`,
  ).join("");

  const headerCells = COLUMNS.map((column, index) =>
    stringCell(`${getColumnName(index + 1)}${headerRowIndex}`, column.label, 2),
  ).join("");

  const dataRows = rows
    .map((row, rowIndex) => {
      const rowNumber = dataStartRowIndex + rowIndex;
      const cells = COLUMNS.map((column, columnIndex) =>
        stringCell(
          `${getColumnName(columnIndex + 1)}${rowNumber}`,
          row[column.key] || "",
          3,
        ),
      ).join("");

      return `<row r="${rowNumber}">${cells}</row>`;
    })
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:${lastColumn}${lastRow}"/>
  <sheetViews>
    <sheetView workbookViewId="0">
      <pane ySplit="4" topLeftCell="A5" activePane="bottomLeft" state="frozen"/>
      <selection pane="bottomLeft" activeCell="A5" sqref="A5"/>
    </sheetView>
  </sheetViews>
  <cols>${columnWidths}</cols>
  <sheetData>
    <row r="1" ht="45" customHeight="1">${stringCell("A1", INFO_LINE_1, 1)}</row>
    <row r="2" ht="38" customHeight="1">${stringCell("A2", INFO_LINE_2, 1)}</row>
    <row r="3"></row>
    <row r="${headerRowIndex}">${headerCells}</row>
    ${dataRows}
  </sheetData>
  <mergeCells count="2">
    <mergeCell ref="A1:${lastColumn}1"/>
    <mergeCell ref="A2:${lastColumn}2"/>
  </mergeCells>
</worksheet>`;
};

const buildWorkbookXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="Free Search Data" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;

const buildWorkbookRelsXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

const buildRootRelsXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const buildContentTypesXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const buildStylesXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="3">
    <font><sz val="11"/><name val="Calibri"/></font>
    <font><b/><sz val="12"/><color rgb="FF1D4ED8"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><color rgb="FF111827"/><name val="Calibri"/></font>
  </fonts>
  <fills count="3">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFEFF6FF"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFE2E8F0"/></left>
      <right style="thin"><color rgb="FFE2E8F0"/></right>
      <top style="thin"><color rgb="FFE2E8F0"/></top>
      <bottom style="thin"><color rgb="FFE2E8F0"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="4">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
    <xf numFmtId="49" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const crcTable = Array.from({ length: 256 }, (_, index) => {
  let crc = index;
  for (let bit = 0; bit < 8; bit += 1) {
    crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
  }
  return crc >>> 0;
});

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = crcTable[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const dosDateTime = () => {
  const now = new Date();
  const time =
    (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1);
  const date =
    ((now.getFullYear() - 1980) << 9) |
    ((now.getMonth() + 1) << 5) |
    now.getDate();
  return { date, time };
};

const pushUint16 = (target, value) => {
  target.push(value & 0xff, (value >>> 8) & 0xff);
};

const pushUint32 = (target, value) => {
  target.push(
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  );
};

const appendBytes = (target, bytes) => {
  for (let index = 0; index < bytes.length; index += 1) {
    target.push(bytes[index]);
  }
};

const createZip = (files) => {
  const output = [];
  const centralDirectory = [];
  const { date, time } = dosDateTime();

  files.forEach(({ name, content }) => {
    const nameBytes = textEncoder.encode(name);
    const dataBytes = textEncoder.encode(content);
    const checksum = crc32(dataBytes);
    const localHeaderOffset = output.length;

    pushUint32(output, 0x04034b50);
    pushUint16(output, 20);
    pushUint16(output, 0);
    pushUint16(output, 0);
    pushUint16(output, time);
    pushUint16(output, date);
    pushUint32(output, checksum);
    pushUint32(output, dataBytes.length);
    pushUint32(output, dataBytes.length);
    pushUint16(output, nameBytes.length);
    pushUint16(output, 0);
    appendBytes(output, nameBytes);
    appendBytes(output, dataBytes);

    pushUint32(centralDirectory, 0x02014b50);
    pushUint16(centralDirectory, 20);
    pushUint16(centralDirectory, 20);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, time);
    pushUint16(centralDirectory, date);
    pushUint32(centralDirectory, checksum);
    pushUint32(centralDirectory, dataBytes.length);
    pushUint32(centralDirectory, dataBytes.length);
    pushUint16(centralDirectory, nameBytes.length);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint32(centralDirectory, 0);
    pushUint32(centralDirectory, localHeaderOffset);
    appendBytes(centralDirectory, nameBytes);
  });

  const centralDirectoryOffset = output.length;
  appendBytes(output, centralDirectory);

  pushUint32(output, 0x06054b50);
  pushUint16(output, 0);
  pushUint16(output, 0);
  pushUint16(output, files.length);
  pushUint16(output, files.length);
  pushUint32(output, centralDirectory.length);
  pushUint32(output, centralDirectoryOffset);
  pushUint16(output, 0);

  return new Uint8Array(output);
};

const safeFileName = (value = "search") => {
  const cleaned = String(value)
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);

  return cleaned || "search";
};

export const downloadFreePeopleData = ({ people = [], prompt = "" }) => {
  if (!Array.isArray(people) || people.length === 0) {
    return false;
  }

  const rows = people.map(buildPersonRow);
  const zipBytes = createZip([
    { name: "[Content_Types].xml", content: buildContentTypesXml() },
    { name: "_rels/.rels", content: buildRootRelsXml() },
    { name: "xl/workbook.xml", content: buildWorkbookXml() },
    { name: "xl/_rels/workbook.xml.rels", content: buildWorkbookRelsXml() },
    { name: "xl/styles.xml", content: buildStylesXml() },
    { name: "xl/worksheets/sheet1.xml", content: buildWorksheetXml(rows) },
  ]);
  const blob = new Blob([zipBytes], { type: XLSX_MIME });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `free-search-data-${safeFileName(prompt)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return true;
};
